import { Box, Link, useMediaQuery } from "@mui/material";
import { useState, useEffect } from "react";
import FileOpenOutlinedIcon from "@mui/icons-material/FileOpenOutlined";
import { getLinkDetails } from "../../utils/global";
import { resolveDomainCode } from "../../utils/bigquery";
import { RELATED_DOCUMENTS_FALLBACK } from "../../data/relatedDocumentsFallback";

// dim_documents has no field marking "this is the metadata/methodology doc" - it has to be
// inferred from the title/filename. Used to pin that document first in Related Documents.
//
// FAOSTAT's own convention is the strongest signal: every domain ships its metadata sheet as
// "<CODE>_e.pdf" in the domain folder (CP\CP_e.pdf, QV\QV_e.pdf), titled after the domain
// rather than with any word like "metadata", so a title-only match misses most of them.
// Ranked rather than yes/no so the domain's own sheet wins over a sibling that also fits the
// loose pattern (CP\CPI_e.pdf is the country notes, not the metadata).
const METADATA_DOC_PATTERN = /metadata|methodolog|description|readme|domain.?information/i;
const FAOSTAT_METADATA_FILE = /\\[a-z]+_e\.pdf$/i;
const metadataRank = (item, domainCode) => {
    const name = item?.file_name || "";
    const title = item?.file_title || "";
    if (domainCode && name.toLowerCase().endsWith(`\\${String(domainCode).toLowerCase()}_e.pdf`)) return 3;
    if (METADATA_DOC_PATTERN.test(title) || METADATA_DOC_PATTERN.test(name)) return 2;
    if (FAOSTAT_METADATA_FILE.test(name)) return 1;
    return 0;
};

export default function MetaData({ descr, dataSource, relatedLinks, itemId, itemDesc, isActive, domain_code, indicatorInfo }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [loadingLastUpdated, setLoadingLastUpdated] = useState(false);
    const MAX_LENGTH = 275;

    // Sources with nothing in dim_documents get a hand-maintained list instead of an empty
    // panel; the moment the database has rows for the domain, those win.
    const fallback = RELATED_DOCUMENTS_FALLBACK[dataSource];
    const documents = relatedLinks?.length > 0
        ? relatedLinks
        : (typeof fallback === "function" ? fallback(indicatorInfo) : fallback) ?? [];
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));

    const toggleReadMore = () => {
        setIsExpanded(!isExpanded);
    };

    // Function to extract DS_ID and T_ID from domain_code
    const extractDomainIds = (domainCode) => {
        if (!domainCode) return null;
        const parts = domainCode.split('.');
        if (parts.length < 3) return null;
        const result = {
            dsId: parts[1],
            tId: parts[2]
        };
        return result;
    };

    // Function to fetch last updated date
    const fetchLastUpdated = async (dsId, tId) => {
        try {
            setLoadingLastUpdated(true);
            // const sql = `SELECT last_modified FROM \`fao-maps-review.fao_cca.mvw_util_lastupdate\` WHERE view_dataset='${dsId}' AND view_name='${tId}'`;
            // The metadata view is copied alongside the views it describes, so a migrated
            // view is looked up in fao_cca_dwh's own copy rather than the legacy one.
            const sql = `SELECT last_modified FROM \`fao-dwh-review.${dsId}.view_source_table_metadata\` WHERE view_dataset='${dsId}' AND view_name='${tId}'`;

            const encodedQuery = encodeURIComponent(sql);
            // const url = `https://fao-cca-api-1036455980974.europe-west3.run.app/bigquery?query=${encodedQuery}&output_format=json&download=false`;
            const url = `https://data.fao.org/api/v2/bigquery?query=${encodedQuery}&output_format=json&download=false`;
            const res = await fetch(url);
            const response = res.ok ? await res.json() : [];

            if (response && response.length > 0 && response[0].last_modified) {
                const date = new Date(response[0].last_modified);
                const day = date.getDate().toString().padStart(2, '0');
                const month = (date.getMonth() + 1).toString().padStart(2, '0');
                const year = date.getFullYear();
                setLastUpdated(`${day}/${month}/${year}`);
            } else {
                setLastUpdated(null);
            }
        } catch (error) {
            console.error('Error fetching last updated date:', error);
            setLastUpdated(null);
        } finally {
            setLoadingLastUpdated(false);
        }
    };

    // useEffect to fetch last updated date when domain_code changes
    useEffect(() => {
        if (domain_code) {
            const domainIds = extractDomainIds(resolveDomainCode(domain_code));
            if (domainIds) {
                fetchLastUpdated(domainIds.dsId, domainIds.tId);
            }
        }
    }, [domain_code]);


    const { href, text } = getLinkDetails(dataSource, itemDesc);

    return (
        <Box
            className="metadata"
            sx={(theme) => ({
                width: { xs: "auto", lg: "28%" },
                mt: { xs: "-20px", lg: "0" },
                py: 2,
                pt: { xs: 1, lg: 0 },
                px: 2,
                border: `1px solid ${theme.palette.secondary.dark}`,
                borderTop: isMobile ? "none" : null,
                backgroundColor: {
                    xs: "secondary.light",
                    //md: isActive() === "active-item" ? 'secondary.light' : 'transparent',
                },
                fontSize: { xs: "12px", xl: "13px" },
                lineHeight: { xs: "18px", xl: "19px" },
            })}
        >
            <>
                <Box className="meta-title" sx={{ color: "primary.main" }}>
                    Description
                </Box>
                <Box>
                    <Box>
                        {descr && descr.length > 0 ? (
                            <>
                                {isExpanded ? (
                                    <Box
                                        component="span"
                                        dangerouslySetInnerHTML={{ __html: descr }}
                                    ></Box>
                                ) : (
                                    <Box
                                        component="span"
                                        dangerouslySetInnerHTML={{
                                            __html: `${descr.slice(0, MAX_LENGTH)}`,
                                        }}
                                    ></Box>
                                )}
                                {descr.length > MAX_LENGTH && (
                                    <Box
                                        onClick={toggleReadMore}
                                        component="span"
                                        sx={{
                                            color: "primary.fontColor",
                                            cursor: "pointer",
                                            fontWeight: "bold",
                                        }}
                                    >
                                        {isExpanded ? " Show less" : " ...Read more"}
                                    </Box>
                                )}
                            </>
                        ) : (
                            <Box>-</Box>
                        )}
                    </Box>
                </Box>
            </>
            <Box display="flex" flexDirection="column">
                {itemId > -1 && (
                    <Box flex="1 1 0" >
                        <Box className="meta-title" sx={{ color: "primary.main" }}>
                            Data Source
                        </Box>
                        <Box>
                            <Box>
                                <Link
                                    href={href}
                                    target="_blank"
                                    title="data source"
                                    rel="noopener noreferrer"
                                >
                                    {text}
                                </Link>
                            </Box>
                        </Box>
                    </Box>
                )}
                {itemId > -1 && (
                    <Box flex="1 1 0" sx={{ mt: 2 }}>
                        <Box className="meta-title" sx={{ color: "primary.main" }}>
                            Source Data Last Accessed On
                        </Box>
                        <Box>
                            {loadingLastUpdated ? (
                                <Box>Loading...</Box>
                            ) : lastUpdated ? (
                                <Box>{lastUpdated}</Box>
                            ) : (
                                <Box>-</Box>
                            )}
                        </Box>
                    </Box>
                )}
                {itemId > -1 && documents.length > 0 ? (
                    <Box flex="1 1 0">
                        <Box className="meta-title" sx={{ color: "primary.main" }}>
                            Related Documents
                        </Box>
                        {[...documents]
                            .sort((a, b) => metadataRank(b, dataSource) - metadataRank(a, dataSource))
                            .map((item, index) => {
                            return (
                                <Box key={index}>
                                    <Box>
                                        <Link
                                            href={item.file_name.startsWith('http') ? item.file_name : `https://files-faostat.fao.org/production/${item.file_name}`}
                                            target="_blank"
                                            title={item.source ?? "FAOSTAT"}
                                            rel="noopener noreferrer"
                                            display="flex"
                                            alignItems="flex-start"
                                            fontSize="12px"
                                            lineHeight="normal"
                                        >
                                            <FileOpenOutlinedIcon
                                                sx={{
                                                    mr: "6px",
                                                    mt: "1px",
                                                    fontSize: "1rem",
                                                    color: "#884d71",
                                                }}
                                            />{" "}{item.file_title}
                                        </Link>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                ) : null}
            </Box>
        </Box>
    );
}
